export const AUTH_NOTICE_QUERY_PARAMS = {
  autoSignInFailed: 'auto-sign-in-failed',
  signupConfirmed: 'signup-confirmed',
  signupSetupIncomplete: 'signup-setup-incomplete',
} as const;

export const AUTH_COPY = {
  login: {
    invalidCredentials: 'Email or password is incorrect.',
    signInFailed: "We couldn't sign you in. Check your connection and try again.",
    sessionExpired: 'Your session expired. Please sign in again.',
    mfaCodeRequired: 'Enter your verification code to finish signing in.',
    mfaCodeRequiredTotp: 'Enter the 6-digit code from your authenticator app.',
    mfaCodeRequiredSms: 'Enter the 6-digit code we texted to your phone.',
    mfaCodeRequiredEmail: 'Enter the 6-digit code we emailed to you.',
    mfaSelectionRequired: 'Choose how you want to verify your identity.',
    mfaSetupUnsupported:
      "Your account needs two-factor authentication set up before you can sign in, and that isn't available on the web portal yet. Please use the Wavvest mobile app or contact support.",
    mfaCodeInvalid: "That code didn't match. Check your authenticator app and try again.",
    mfaSessionExpired: 'That sign-in attempt timed out. Please enter your password again.',
    autoSignInFailed: 'We could not sign you in automatically. Please sign in.',
    signupConfirmed: 'Your email is verified. Sign in to continue.',
    // The signup flow sends clients here when the code was accepted but the
    // account link afterwards failed. Login owns the rest of the explanation
    // through its account setup support flow, so keep this to one line.
    signupSetupIncomplete:
      "Your email is verified, but we couldn't finish setting up your account. Sign in to continue.",
    legacyPasswordResetRequired: 'Reset your password to continue.',
    legacyPasswordResetDescription:
      'This account was created with an older invitation sign-in method. We no longer use that password setup screen. Send a reset code to your email, then create a new password to sign in.',
    unconfirmedAccount:
      'Your email still needs to be verified with the code we sent during signup.',
    unconfirmedAccountNeedsSignupVerification:
      "We couldn't verify this email from here. Use the verification code from your signup email to finish signup.",
  },
  accountSetup: {
    finishing: 'Your signup is still finishing. Please try again in a minute.',
    profileLoadFailed:
      "We couldn't load your account details. Refresh the page or try again later.",
    supportTitle: "We couldn't open your client portal profile.",
    supportBody:
      "Your sign-in worked, but your profile was not linked correctly. Contact our team and we'll fix the account link.",
    supportNotifiedTitle: 'Our team has been notified',
    setupIncompleteSupportNotifiedBody:
      "Your sign-in worked, but account setup is still incomplete. We've sent the details we need to investigate this, and are working on a fix.",
    profileSupportNotifiedBody:
      "Your sign-in worked, but we couldn't open your client portal profile. We've sent the details we need to investigate this, and are working on a fix.",
    supportNotificationFailure: "We couldn't notify our team. Please try again.",
    actions: {
      contactTeam: 'Contact our team',
      notified: 'Team notified',
    },
  },
  signup: {
    accountExists:
      "We couldn't send a verification code to that email. Check the email address and try again.",
    passwordRequirements: 'Create a password that meets the listed requirements.',
    invalidDetails: 'Check your email and password, then try again.',
    tooManyAttempts: 'Too many attempts. Wait a few minutes, then try again.',
    codeDeliveryFailed:
      "We couldn't send a confirmation code. Check the email address or try again.",
    serviceUnavailable:
      "We couldn't reach the signup service. Check your connection and try again.",
    failed: "We couldn't create your account. Check your details and try again.",
  },
  confirmation: {
    codeMismatch: 'That confirmation code is not correct. Check the code and try again.',
    expiredCode: 'That confirmation code expired. Choose Resend Code to get a new one.',
    tooManyAttempts: 'Too many attempts. Wait a few minutes before trying again.',
    alreadyConfirmed: 'This email is already verified. Sign in to continue.',
    signupMissing: "We couldn't find that signup. Ask your advisor for a new invitation.",
    invalidCode: 'Enter the 6-digit code from your email.',
    failed: "We couldn't verify that code. Check it and try again.",
  },
  resendCode: {
    tooManyAttempts: 'Too many attempts. Wait a few minutes before requesting another code.',
    invalidEmail: 'Enter a valid email address, then try again.',
    signupMissing: "We couldn't find that signup. Ask your advisor for a new invitation.",
    failed: "We couldn't resend the code. Check your email and try again.",
  },
  passwordReset: {
    codeSent: 'Reset code sent. Check your email for the code.',
    sendFailed: "We couldn't send a reset code. Check your email and try again.",
    userNotFound: "We couldn't send a reset code for that email. Check the email and try again.",
    tooManyAttempts: 'Too many attempts. Wait a few minutes before trying again.',
    invalidEmail: 'Enter a valid email address, then try again.',
    resetSuccess: 'Password reset successfully. Sign in with your new password.',
    invalidCode: 'That reset code is not correct. Check the code and try again.',
    expiredCode: 'That reset code expired. Send a new code and try again.',
    invalidPassword: 'Create a password that meets the listed requirements.',
    resetFailed: "We couldn't reset your password. Check the code and try again.",
  },
} as const;
